import React, { useMemo, useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { getBoxEmployeeName, isBoxAccount } from "./utils/userProfile";

const USER_TO_EMPLOYEE = {
  "Baneen@123": "بنين",
  "sara@123": "سارة",
  "jumana@123": "جومانا",
  "maysa@123": "مياسة",
  "designer@123": "المصمم",
  "maryam@123": "مريم",
    "farah@123": "فرح",
  "halima@123": "حليمة",
    "karina@123": "تيهان",
  "mohamed@123": "محمد",
  "art@1234": "عبد الرحمن",
};

const EMPLOYEES = Object.values(USER_TO_EMPLOYEE);

// ⏰ خيارات دروب داون الساعة (نفس فكرة لوحة المدير) - من 9 الصبح إلى 4 العصر
// بفواصل ربع ساعة (00/15/30/45)، ضمن ساعات الدوام
const HOUR_OPTIONS = [];
for (let hour = 9; hour <= 16; hour++) {
  for (let minute = 0; minute < 60; minute += 15) {
    if (hour === 16 && minute > 0) break; // الدوام يوقف الساعة 16:00 بالضبط
    HOUR_OPTIONS.push(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
  }
}

const formatHourOption = (t) => {
  const parsed = dayjs(t, "HH:mm");
  return parsed.isValid() ? parsed.format("h:mm A") : t;
};

const toMinutes = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export default function LeaveRequest() {
      const navigate = useNavigate();
  const location = useLocation();

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    // حسابات البوكسات ترجع للوحتها، والباقي الافتراضي لمحمد
    return params.get("returnTo") || (isBoxAccount() ? "/workspace" : "/mohamed");
  }, [location.search]);


  // ✅ يقرأ username من الرابط: ?u=Baneen@123
  const employeeFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const u = params.get("u") || "";
    // حسابات البوكسات (الجديدة مثل تيهان): اسمها الظاهر من بوكس "طلب إجازة"
    // (حتى لو حساب قديم انحوّل - مثل كارينا ← تيهان - ياخذ الاسم الجديد)
    // ولو انفتحت من بوكس "طلب إجازة" بدون رابط ?u= لحساب قديم ← اسمه من حسابه
    return (
      getBoxEmployeeName() ||
      USER_TO_EMPLOYEE[u] ||
      USER_TO_EMPLOYEE[localStorage.getItem("username")] ||
      ""
    );
  }, [location.search]);

  const [employee, setEmployee] = useState(""); // اسم الموظف (ثابت)
  const allowedEmployees = useMemo(
    () => [...EMPLOYEES, getBoxEmployeeName()].filter(Boolean),
    []
  );
  const [type, setType] = useState(""); // hourly | fullDay | ""
  const [date, setDate] = useState(""); // "YYYY-MM-DD"
  const [fromTime, setFromTime] = useState(""); // "HH:mm"
  const [toTime, setToTime] = useState("");
  const [reason, setReason] = useState("");

  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ ثبت الموظف من الرابط
  useEffect(() => {
    if (employeeFromUrl && allowedEmployees.includes(employeeFromUrl)) {
      setEmployee(employeeFromUrl);
    } else {
      setEmployee("");
    }
  }, [employeeFromUrl]);

  // ✅ إذا غير النوع، نظف الوقت
  useEffect(() => {
    if (type !== "hourly") {
      setFromTime("");
      setToTime("");
    }
  }, [type]);

  const hours = useMemo(() => {
    if (type !== "hourly" || !fromTime || !toTime) return 0;
    const diff = (toMinutes(toTime) - toMinutes(fromTime)) / 60;
    return diff > 0 ? diff : 0;
  }, [type, fromTime, toTime]);

  const validate = () => {
    setError("");

    if (!employee || !allowedEmployees.includes(employee)) {
      return "❌ اسم الموظف غير صحيح (تأكد من زر الدخول/الرابط).";
    }

    if (!type) {
      return "❌ اختر نوع الإجازة (زمنية أو يوم كامل).";
    }

    const today = dayjs().startOf("day");
    if (!date || dayjs(date).startOf("day").isBefore(today)) {
      return "❌ اختر تاريخ صحيح (ما يقبل تاريخ قديم).";
    }

    if (!reason.trim()) {
      return "❌ اكتب السبب قبل الإرسال.";
    }

    if (type === "hourly") {
      if (!fromTime || !toTime) return "❌ لازم تختار وقت من وإلى.";

      if (toMinutes(fromTime) < toMinutes("09:00") || toMinutes(toTime) > toMinutes("16:00")) {
        return "❌ الوقت لازم يكون ضمن الدوام (09:00 إلى 16:00).";
      }

      if (toMinutes(toTime) <= toMinutes(fromTime)) {
        return "❌ وقت (إلى) لازم يكون بعد وقت (من).";
      }

      if (hours <= 0) return "❌ عدد الساعات غير صحيح.";
    }

    return "";
  };

  const handleSubmit = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        employee,
        type,
        date: dayjs(date).format("YYYY-MM-DD"),
        reason: reason.trim(),
      };

      if (type === "hourly") {
        payload.fromTime = fromTime;
        payload.toTime = toTime;
        payload.hours = Number(hours.toFixed(2));
      }

      const res = await apiFetch(`${API_BASE}/hr/leave-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل إرسال طلب الإجازة");
      }

      // ✅ reset كامل (تبقى فقط employee ثابت)
      setType("");
      setDate("");
      setFromTime("");
      setToTime("");
      setReason("");
      setError("");
      setSuccessOpen(true);
    } catch (e) {
      console.error(e);
      setError("❌ صار خطأ أثناء إرسال الطلب.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container dir="rtl" sx={{ mt: 3, mb: 4 }}>
      <Box
        sx={{
          maxWidth: 1000,
          mx: "auto",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h4" fontWeight={900}>
            📝 طلب إجازة
          </Typography>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={`الموظف: ${employee || "—"}`}
              color="primary"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label="دوام: 09:00 - 16:00"
              color="info"
              sx={{ fontWeight: 700 }}
            />
            {type === "hourly" && hours > 0 && (
              <Chip
                label={`عدد الساعات: ${hours}`}
                color="success"
                sx={{ fontWeight: 800 }}
              />
            )}
          </Box>
        </Box>
<Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
  <Button
    variant="outlined"
    onClick={() => navigate(returnTo)}
    sx={{ fontWeight: 800, borderRadius: 2, px: 3 }}
  >
    ⬅️ رجوع  
  </Button>
</Box>

        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            boxShadow: 3,
          }}
        >
          <Box mb={2}>
            <Typography fontWeight={800} mb={1}>
              نوع الإجازة
            </Typography>
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{ gap: 2 }}
            >
              <FormControlLabel value="hourly" control={<Radio />} label="زمنية" />
              <FormControlLabel value="fullDay" control={<Radio />} label="يوم كامل" />
            </RadioGroup>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                type="date"
                label="التاريخ"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
              />
            </Grid>

            {type === "hourly" && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ right: 0, left: "auto" }}>من الساعة</InputLabel>
                    <Select
                      label="من الساعة"
                      value={fromTime}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFromTime(v);
                        if (toTime && toTime <= v) setToTime("");
                      }}
                    >
                      {HOUR_OPTIONS.map((t) => (
                        <MenuItem key={t} value={t}>{formatHourOption(t)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="medium" disabled={!fromTime}>
                    <InputLabel sx={{ right: 0, left: "auto" }}>إلى الساعة</InputLabel>
                    <Select
                      label="إلى الساعة"
                      value={toTime}
                      onChange={(e) => setToTime(e.target.value)}
                    >
                      {HOUR_OPTIONS.filter((t) => !fromTime || t > fromTime).map((t) => (
                        <MenuItem key={t} value={t}>{formatHourOption(t)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="السبب"
                multiline
                minRows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
          </Grid>

          {error && (
            <Box mt={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          <Box mt={3}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                py: 1.3,
                fontWeight: 900,
                borderRadius: 2.5,
                fontSize: 16,
              }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "جارٍ الإرسال..." : "✅ Submit"}
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={successOpen}
          autoHideDuration={2500}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success" variant="filled">
            ✅ تم إرسال طلب الإجازة للمدير
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
